import { BROWSER_STORAGE_KEYS } from '@lib/data/browser-storage-keys'
import { IdentifierMode } from '@lib/utils/identifier'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import { NextPage } from 'next'
import React, { useState } from 'react'
import { AlertTriangle } from 'react-feather'
import DottedCardBackgroundWrapper from '../common/dotted-card-bg'
import IdentifierInput from './identifier-input'
import LoginButton from './password-login-btn'
import TestimonialCard from './testimonial-card'

type Props = {
  phone: string
  mode: IdentifierMode
  loading: boolean
  errors: any
  handlePhoneInputChange: (value: string, mode: IdentifierMode) => void
  handlePhoneInputFormSubmit: () => void
  redirectToPasswordPage: () => void
}

const TestimonialCardWithDottedBackground = DottedCardBackgroundWrapper(
  TestimonialCard
)

const EnterPhoneInput: NextPage<Props> = ({
  loading,
  phone,
  mode,
  handlePhoneInputChange,
  handlePhoneInputFormSubmit,
  errors,
  redirectToPasswordPage,
}) => {
  // What the field has decided it is, reported up by `IdentifierInput` so the
  // caption can narrow with it. Neutral until the widget commits - a leading
  // digit is not yet evidence of a phone number.
  const [identifierState, setIdentifierState] = useState<
    'neutral' | 'phone' | 'email'
  >('neutral')
  // This is required to to decide when CTA should be the primary one
  let hasPassword: boolean
  const hasPasswordKey = getFromLocalStorage(BROWSER_STORAGE_KEYS.HAS_PASSWORD)

  if (hasPasswordKey) {
    hasPassword = hasPasswordKey === 'true' ? true : false
  }

  // Not gated on the identifier any more: the password endpoints take an
  // email as well since CE-8582, so an email signup that has set a password
  // can use it. `hasPassword` only says this browser has set one before, so
  // it decides which CTA leads, not whether password login is possible.
  const canLoginWithPassword = hasPassword

  const handleEnterOnInput = () => {
    if (canLoginWithPassword) {
      redirectToPasswordPage()
    } else {
      handlePhoneInputFormSubmit()
    }
  }

  return (
    <div className="flex items-start justify-between flex-1 h-full bg-white lg:items-center lg:bg-transparent">
      <div className="container mx-auto">
        <div className="flex flex-wrap items-center">
          <div className="hidden w-full pr-5 lg:block sm:w-1/2">
            <TestimonialCardWithDottedBackground />
          </div>
          <div className="w-full px-4 lg:w-1/2">
            <div className="px-5">
              <div className="leading-tight text-grey text-5xl font-700">
                <p>Let&apos;s get started</p>
              </div>
              <div className="max-w-md my-5 mt-10">
                <div>
                  <label className="mb-1 text-grey font-500">
                    {identifierState === 'phone'
                      ? 'Enter your phone number'
                      : identifierState === 'email'
                      ? 'Enter your email address'
                      : 'Enter your phone number or email'}
                  </label>
                  <IdentifierInput
                    value={phone}
                    mode={mode}
                    onIdentifierStateChange={setIdentifierState}
                    onChange={handlePhoneInputChange}
                    onEnter={handleEnterOnInput}
                    placeholder="Phone number or email"
                    autoFocus
                    inputId="phone"
                  />
                </div>
                {errors.phone && (
                  <div className="flex items-center space-x-2 bg-red-50 border-l-4 border-orange py-1 mt-2">
                    <AlertTriangle size={15} className="text-red-200 ml-2" />
                    <p className="text-sm inline-block">{errors.phone}</p>
                  </div>
                )}
                {errors.nonFieldErrors && (
                  <div className="flex items-center space-x-2 bg-red-50 border-l-4 border-orange py-1 mt-2">
                    <AlertTriangle size={15} className="text-red-200 ml-2" />
                    <p className="text-sm inline-block">
                      {errors.nonFieldErrors}
                    </p>
                  </div>
                )}
                {errors.detail && (
                  <div className="flex items-center space-x-2 bg-red-50 border-l-4 border-orange py-1 mt-2">
                    <AlertTriangle size={15} className="text-red-200 ml-2" />
                    <p className="text-sm inline-block">{errors.detail}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-5 mt-10">
                  {canLoginWithPassword && (
                    <>
                      <LoginButton
                        isDisabled={loading || !phone}
                        isLoading={loading}
                        label="Login with password"
                        handleClick={redirectToPasswordPage}
                        isPrimary
                      />
                      <LoginButton
                        isDisabled={loading || !phone}
                        isLoading={loading}
                        label="Login with OTP"
                        handleClick={handlePhoneInputFormSubmit}
                      />
                    </>
                  )}
                  {!canLoginWithPassword && (
                    <>
                      <LoginButton
                        isDisabled={loading || !phone}
                        isLoading={loading}
                        label="Login with OTP"
                        handleClick={handlePhoneInputFormSubmit}
                        isPrimary
                      />
                      <LoginButton
                        isDisabled={loading || !phone}
                        isLoading={loading}
                        label="Login with password"
                        handleClick={redirectToPasswordPage}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-center w-full h-auto my-16 lg:hidden lg:w-1/2 md:px-5">
              <TestimonialCard />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EnterPhoneInput
